import { Graphics } from 'pixi.js';

import type { WorldModel } from '../world/model';
import {
  tileToIso,
  TILE_WIDTH_PX,
  TILE_HEIGHT_PX,
  visibleTileRange,
  type CameraState,
} from './iso';
import { moistureDarken } from './palette';
import type { SceneLayers } from './scene';

export interface MoistureOverlayRenderer {
  render: (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
  ) => void;
  destroy: () => void;
}

export function createMoistureOverlayRenderer(
  layers: SceneLayers,
): MoistureOverlayRenderer {
  const g = new Graphics();
  layers.floorOverlayLayer.addChild(g);

  const halfW = TILE_WIDTH_PX / 2;
  const halfH = TILE_HEIGHT_PX / 2;

  const render = (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
  ): void => {
    g.clear();
    if (!model.moistureReceived) return;

    const r = visibleTileRange(cam, viewportW, viewportH, 1);

    for (let ty = r.minY; ty <= r.maxY; ty++) {
      for (let tx = r.minX; tx <= r.maxX; tx++) {
        if (tx < 0 || tx >= model.width || ty < 0 || ty >= model.height) continue;
        const idx = ty * model.width + tx;
        const moisture = model.tileMoisture[idx]!;
        if (moisture === 0) continue;

        const vis = moistureDarken(moisture);
        const iso = tileToIso(tx, ty);

        g.poly([
          iso.x, iso.y - halfH,
          iso.x + halfW, iso.y,
          iso.x, iso.y + halfH,
          iso.x - halfW, iso.y,
        ]);
        g.fill({ color: 0x000000, alpha: vis.darken });

        if (vis.puddle) {
          g.ellipse(iso.x, iso.y, halfW * 0.35, halfH * 0.35);
          g.fill({ color: 0x2a5c8a, alpha: 0.35 });
        }
      }
    }
  };

  return {
    render,
    destroy: () => g.destroy(),
  };
}
